import { useMemo } from 'react';
import { NormalizedGroup } from '../../../../../../redux/structure/GroupsAdapter';
import useCurrentWorkspace from '../../../../../../redux/workspace/hooks/useCurrentWorkspace';
import { HierarchyLevel } from '../../../../../../redux/structure/util/getTypeFromGroup';

function useHierarchyLabel(
  entity: NormalizedGroup | undefined,
  childGroups: NormalizedGroup[] | undefined,
  marketId?: string
): string | undefined {
  const { data: workspace } = useCurrentWorkspace(marketId);

  const hierarchy: HierarchyLevel[] | undefined = useMemo(() => {
    const raw = workspace?.settings?.['flexeserveUI.hierarchy'];
    if (!raw) return undefined;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed as HierarchyLevel[];
    } catch {
      // ignore invalid JSON
    }
    return undefined;
  }, [workspace?.settings]);

  return useMemo(() => {
    if (!hierarchy) return undefined;

    // If current group has FS_LEVEL, show the next level's label
    const currentLevel = entity?.attributes?.['FS_LEVEL'];
    if (currentLevel !== undefined) {
      const currentId = Number(currentLevel);
      const nextLevel = hierarchy.find((h) => h.id === currentId + 1);
      if (nextLevel) return nextLevel.label;
    }

    // Otherwise, find the most common FS_LEVEL among child groups
    if (childGroups?.length) {
      const levelCounts: Record<string, number> = {};
      for (const group of childGroups) {
        const level = group.attributes?.['FS_LEVEL'];
        if (level !== undefined) {
          levelCounts[level] = (levelCounts[level] || 0) + 1;
        }
      }
      const entries = Object.entries(levelCounts);
      if (entries.length > 0) {
        const [mostCommonLevel] = entries.reduce((a, b) =>
          a[1] >= b[1] ? a : b
        );
        const found = hierarchy.find((h) => h.id === Number(mostCommonLevel));
        if (found) return found.label;
      }
    }

    return undefined;
  }, [hierarchy, entity, childGroups]);
}

export default useHierarchyLabel;
